import { resolveBuiltinAgentControllerDefinition } from "../agents/builtin-agent-controller-definitions";
import {
  type AgentsRegistryAgent,
  type AgentsRegistryLifecycleStatus,
  type AgentsRegistryTransportMode,
  evaluateRegistryDetection,
  listBuiltinAgentsRegistryEntries,
  resolveRegistryTemplate,
} from "../agents/registry-source";
import type {
  RuntimeAgentStreamingMode,
  RuntimeAgentTransportMode,
  RuntimeSkillsConfig,
} from "../config/runtime-config";

export type SkillsAgentTransportMode = {
  mode: RuntimeAgentTransportMode;
  label: string;
  description: string;
};

export type SkillsAgentRegistryEntry = {
  name: string;
  displayName: string;
  skillsDir: string;
  globalSkillsDir?: string;
  detectInstalled: () => Promise<boolean>;
  lifecycleStatus: AgentsRegistryLifecycleStatus;
  runtimeSupported: boolean;
  skillsInstallable: boolean;
  transportModes: SkillsAgentTransportMode[];
  defaultTransportMode?: RuntimeAgentTransportMode;
  defaultStreamingMode?: RuntimeAgentStreamingMode;
};

const SUPPORTED_RUNTIME_TRANSPORT_MODES = new Set<RuntimeAgentTransportMode>([
  "local-cli",
  "codex-app-server",
]);
const TRANSPORT_MODE_METADATA: Record<
  RuntimeAgentTransportMode,
  { label: string; description: string }
> = {
  "local-cli": {
    label: "Local CLI (Default)",
    description:
      "Run via local CLI on this computer, with session continuation when available.",
  },
  "codex-app-server": {
    label: "Codex App Server",
    description:
      "Run Codex through the local Codex App Server with native thread control.",
  },
};
const SUPPORTED_RUNTIME_STREAMING_MODES = new Set<RuntimeAgentStreamingMode>([
  "final",
  "stream",
]);
const DEFAULT_AGENT_DETECTION_PROBE_TIMEOUT_MS = 3000;
const MAX_CONCURRENT_AGENT_DETECTIONS = 4;

export function resolveSkillsAgentRegistry(
  skillsConfig?: RuntimeSkillsConfig | null
): Record<string, SkillsAgentRegistryEntry> {
  const overrides = skillsConfig?.agents ?? {};
  const merged: Record<string, SkillsAgentRegistryEntry> = {};

  for (const agent of listBuiltinAgentsRegistryEntries()) {
    const override = overrides[agent.id];
    const overrideSkillsDir = normalizeOptionalNonEmptyString(
      override?.skillsDir
    );
    const overrideGlobalSkillsDir = normalizeOptionalNonEmptyString(
      override?.globalSkillsDir
    );
    const transportModes = resolveAgentTransportModes(agent);
    const defaultTransportMode = resolveDefaultTransportMode(
      agent,
      transportModes
    );
    const defaultStreamingMode = resolveDefaultStreamingMode(agent);

    merged[agent.id] = {
      name: agent.id,
      displayName: agent.displayName,
      skillsDir: overrideSkillsDir ?? resolveRegistryTemplate(agent.skillsDir),
      ...(overrideGlobalSkillsDir
        ? { globalSkillsDir: overrideGlobalSkillsDir }
        : resolveRegistryGlobalSkillsDir(agent.globalSkillsDir)),
      detectInstalled: async () => detectInstalledBuiltinAgent(agent),
      ...resolveRegistryMetadata(agent),
      transportModes,
      ...(defaultTransportMode ? { defaultTransportMode } : {}),
      ...(defaultStreamingMode ? { defaultStreamingMode } : {}),
    };
  }

  return merged;
}

export async function detectInstalledSkillsAgents(
  registry: Record<string, SkillsAgentRegistryEntry>
): Promise<string[]> {
  const checks = await mapWithConcurrency(
    Object.entries(registry),
    MAX_CONCURRENT_AGENT_DETECTIONS,
    async ([agentName, config]) => ({
      agentName,
      installed: await config.detectInstalled(),
    })
  );

  return checks
    .filter((entry) => entry.installed)
    .map((entry) => entry.agentName)
    .sort((a, b) => a.localeCompare(b));
}

async function detectInstalledBuiltinAgent(
  agent: AgentsRegistryAgent
): Promise<boolean> {
  const auxiliaryDetected = evaluateRegistryDetection(agent.detection);
  if (!(agent.category === "builtin" && agent.runtime.supported)) {
    return auxiliaryDetected;
  }

  const installResolver = resolveBuiltinAgentControllerDefinition(
    agent.id
  )?.installResolver;
  if (!installResolver) {
    return auxiliaryDetected;
  }

  const installedCandidates =
    await installResolver.resolveInstalledLaunchCandidates({
      timeoutMs: resolveAgentDetectionProbeTimeoutMs(),
    });
  return installedCandidates.ok && installedCandidates.candidates.length > 0;
}

async function mapWithConcurrency<TInput, TOutput>(
  inputs: TInput[],
  maxConcurrency: number,
  mapper: (input: TInput) => Promise<TOutput>
): Promise<TOutput[]> {
  const results = new Array<TOutput>(inputs.length);
  let nextIndex = 0;
  const workerCount = Math.max(1, Math.min(maxConcurrency, inputs.length));
  await Promise.all(
    Array.from({ length: workerCount }, async () => {
      for (;;) {
        const currentIndex = nextIndex;
        nextIndex += 1;
        const input = inputs[currentIndex];
        if (input === undefined) {
          return;
        }
        results[currentIndex] = await mapper(input);
      }
    })
  );
  return results;
}

function resolveRegistryMetadata(
  agent: AgentsRegistryAgent
): Pick<
  SkillsAgentRegistryEntry,
  "lifecycleStatus" | "runtimeSupported" | "skillsInstallable"
> {
  return {
    lifecycleStatus: agent.lifecycle.status,
    runtimeSupported: agent.runtime.supported,
    skillsInstallable: agent.service.skillsInstallable,
  };
}

function resolveRegistryGlobalSkillsDir(value: string | string[] | undefined): {
  globalSkillsDir?: string;
} {
  if (typeof value === "string") {
    const resolved = normalizeOptionalNonEmptyString(
      resolveRegistryTemplate(value)
    );
    return resolved ? { globalSkillsDir: resolved } : {};
  }
  if (!Array.isArray(value)) {
    return {};
  }
  for (const candidate of value) {
    const resolved = normalizeOptionalNonEmptyString(
      resolveRegistryTemplate(candidate)
    );
    if (resolved) {
      return { globalSkillsDir: resolved };
    }
  }
  return {};
}

function resolveAgentTransportModes(
  agent: AgentsRegistryAgent
): SkillsAgentTransportMode[] {
  if (!agent.runtime.supported) {
    return [];
  }
  const adapterModes = new Set<RuntimeAgentTransportMode>();
  for (const adapter of agent.runtime.adapters) {
    for (const mode of adapter.transportModes) {
      if (isRuntimeTransportMode(mode)) {
        adapterModes.add(mode);
      }
    }
  }
  return [...adapterModes].map((mode) => {
    const metadata = TRANSPORT_MODE_METADATA[mode];
    return {
      mode,
      label: metadata.label,
      description: metadata.description,
    };
  });
}

function resolveDefaultTransportMode(
  agent: AgentsRegistryAgent,
  transportModes: SkillsAgentTransportMode[]
): RuntimeAgentTransportMode | undefined {
  if (transportModes.length === 0) {
    return undefined;
  }
  const configuredDefaultMode = agent.defaultTransportMode;
  if (
    configuredDefaultMode &&
    isRuntimeTransportMode(configuredDefaultMode) &&
    transportModes.some(
      (transportMode) => transportMode.mode === configuredDefaultMode
    )
  ) {
    return configuredDefaultMode;
  }
  return transportModes[0]?.mode;
}

function resolveDefaultStreamingMode(
  agent: AgentsRegistryAgent
): RuntimeAgentStreamingMode | undefined {
  const configured = agent.defaultStreamingMode;
  if (configured && isRuntimeStreamingMode(configured)) {
    return configured;
  }
  return undefined;
}

function isRuntimeTransportMode(
  value: AgentsRegistryTransportMode
): value is RuntimeAgentTransportMode {
  return SUPPORTED_RUNTIME_TRANSPORT_MODES.has(
    value as RuntimeAgentTransportMode
  );
}

function isRuntimeStreamingMode(
  value: unknown
): value is RuntimeAgentStreamingMode {
  return SUPPORTED_RUNTIME_STREAMING_MODES.has(
    value as RuntimeAgentStreamingMode
  );
}

function resolveAgentDetectionProbeTimeoutMs(): number {
  const raw = normalizeOptionalNonEmptyString(
    process.env.WAKELOOP_AGENT_DETECTION_PROBE_TIMEOUT_MS
  );
  if (!raw) {
    return DEFAULT_AGENT_DETECTION_PROBE_TIMEOUT_MS;
  }
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0
    ? parsed
    : DEFAULT_AGENT_DETECTION_PROBE_TIMEOUT_MS;
}

function normalizeOptionalNonEmptyString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}
